/**
 * Timezone-aware event formatters.
 *
 * Why this file exists:
 *   Event docs ship two time fields — `utc_time` (the canonical absolute
 *   moment) and `local_time` (a naive ISO string in the team's timezone).
 *   The old code did `new Date(event.local_time)` which JavaScript parses
 *   as the BROWSER's local time. That made a coach in PT and a parent in
 *   MT see different clock times for the same event — exactly the bug
 *   the user reported as "games in coach doesn't match parent view".
 *
 * The fix:
 *   Always render from `utc_time` (or fall back to start_utc / local_time)
 *   formatted in the team's timezone using `Intl.DateTimeFormat`. The
 *   user can override their display timezone in Settings, but the default
 *   is whatever the team's timezone is — that way "Tue 6pm" means
 *   exactly the same wall-clock moment for everyone on the team,
 *   regardless of where each member happens to be.
 *
 * All exported helpers are pure / synchronous so they're safe in any
 * React render path.
 */

/** Browser default — used when neither team nor user override is known. */
export function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Resolve the timezone to render an event in.
 *
 *   1. If the user has explicitly set a `timezone` override, use it.
 *   2. Otherwise use the team's timezone (carried on each event response).
 *   3. Last-resort fallback is the browser's detected zone — only relevant
 *      for events that predate the timezone field.
 */
export function resolveEventTimezone(event, user) {
  if (user && user.timezone) return user.timezone;
  if (event && event.timezone) return event.timezone;
  return detectBrowserTimezone() || "America/Los_Angeles";
}

/** Build a Date object representing the canonical UTC moment of an event. */
function _eventDate(event) {
  // utc_time is the source of truth — an RFC3339 string with a Z or offset.
  // local_time is naive (no offset) and only kept for legacy sort/compare.
  const raw = event && (event.utc_time || event.start_utc);
  if (raw) return new Date(raw);
  // Last-ditch: parse local_time and assume it was meant in the team TZ.
  // This is approximate; new events all carry utc_time so this branch is
  // only ever hit for very old documents.
  if (event && event.local_time) return new Date(event.local_time);
  return null;
}

const _baseDateOpts = { weekday: "short", month: "short", day: "numeric" };
const _baseTimeOpts = { hour: "numeric", minute: "2-digit" };

/** "Sat, Jun 1 · 6:00 PM PT" — the canonical "when" string. */
export function formatEventWhen(event, user) {
  const d = _eventDate(event);
  if (!d) return "";
  const tz = resolveEventTimezone(event, user);
  try {
    const date = new Intl.DateTimeFormat(undefined, { ..._baseDateOpts, timeZone: tz }).format(d);
    const time = new Intl.DateTimeFormat(undefined, { ..._baseTimeOpts, timeZone: tz }).format(d);
    const tzShort = _shortTzLabel(d, tz);
    return tzShort ? `${date} · ${time} ${tzShort}` : `${date} · ${time}`;
  } catch {
    return d.toLocaleString();
  }
}

/** Date-only label, e.g. "Sat, Jun 1" — for grouping headers. */
export function formatEventDate(event, user) {
  const d = _eventDate(event);
  if (!d) return "";
  const tz = resolveEventTimezone(event, user);
  try {
    return new Intl.DateTimeFormat(undefined, { ..._baseDateOpts, timeZone: tz }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

/** Time-only label, e.g. "6:00 PM" — for compact rows. */
export function formatEventTime(event, user) {
  const d = _eventDate(event);
  if (!d) return "";
  const tz = resolveEventTimezone(event, user);
  try {
    return new Intl.DateTimeFormat(undefined, { ..._baseTimeOpts, timeZone: tz }).format(d);
  } catch {
    return d.toLocaleTimeString([], _baseTimeOpts);
  }
}

/**
 * Comparable millisecond key for sorting events in their canonical UTC
 * order. Using `new Date(local_time)` would silently re-interpret naive
 * strings in the browser's TZ which would mis-sort cross-team feeds.
 */
export function eventSortKey(event) {
  const d = _eventDate(event);
  return d ? d.getTime() : 0;
}

function _shortTzLabel(date, tz) {
  // Try to extract the "PT" / "ET" short label so users know which zone
  // they're seeing. Falls back to silent if the runtime can't generate it.
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: tz, timeZoneName: "short", hour: "numeric",
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart ? tzPart.value : "";
  } catch {
    return "";
  }
}

/**
 * Curated list of timezones for the Settings picker. Common North American
 * + international zones — enough coverage for ChalkOS's target market
 * without overwhelming users with an alphabetical IANA dump.
 */
export const TIMEZONE_OPTIONS = [
  { value: "America/Los_Angeles", label: "Pacific Time — Los Angeles" },
  { value: "America/Denver", label: "Mountain Time — Denver" },
  { value: "America/Phoenix", label: "Mountain Time (no DST) — Phoenix" },
  { value: "America/Chicago", label: "Central Time — Chicago" },
  { value: "America/New_York", label: "Eastern Time — New York" },
  { value: "America/Anchorage", label: "Alaska Time — Anchorage" },
  { value: "Pacific/Honolulu", label: "Hawaii Time — Honolulu" },
  { value: "America/Toronto", label: "Eastern Time — Toronto" },
  { value: "America/Vancouver", label: "Pacific Time — Vancouver" },
  { value: "America/Mexico_City", label: "Central Time — Mexico City" },
  { value: "Europe/London", label: "United Kingdom — London" },
  { value: "Europe/Dublin", label: "Ireland — Dublin" },
  { value: "Europe/Paris", label: "Central Europe — Paris" },
  { value: "Europe/Berlin", label: "Central Europe — Berlin" },
  { value: "Australia/Sydney", label: "Australia — Sydney" },
  { value: "Asia/Tokyo", label: "Japan — Tokyo" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "UTC", label: "UTC" },
];
